import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@pazportal/ui';
import { Freshness } from '../components/Freshness';
import { useLocations, type LocationWithSummary } from '../hooks/useLocations';
import { OPEN_TICKET_STATUSES, useTickets } from '../hooks/useTickets';
import {
  aggregateUnifi,
  unifiByLocation,
  useUnifiSites,
  UNIFI_EXPECTED_REFRESH_MS,
  UNIFI_STALE_MS,
  type UnifiAggregate,
} from '../hooks/useUnifiSites';
import { useAccount } from '../contexts/AccountContext';
import { SkeletonCard } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { BrandLogo } from '../components/BrandLogo';
import { PropertyGlobe, type GlobeMarker } from '../components/PropertyGlobe';
import { PropertyDetailPanel } from '../components/PropertyDetailPanel';
import { PortfolioTripCalendar } from '../components/PortfolioTripCalendar';
import { usePropertyCatalog } from '../hooks/usePropertyCatalog';
import { codesWithVisits, resolvePropertyCode } from '../lib/propertyCatalog';
import { useI18n, type Translate } from '../i18n/I18nProvider';
import { deriveSiteStatus } from '../lib/siteStatus';
import { formatWanUptime } from '../lib/wanUptime';

function networkLabel(network: UnifiAggregate | null | undefined, t: Translate): string {
  const uptime = formatWanUptime(network?.wanUptimePct);
  const status = deriveSiteStatus(
    {
      telemetry: network
        ? {
            totalDevices: network.totalDevices,
            onlineDevices: network.onlineDevices,
            offlineDevices: network.offlineDevices,
            gatewayDevices: network.gatewayDevices,
            offlineGatewayDevices: network.offlineGatewayDevices,
            latestUpdatedAt: network.latestUpdatedAt,
          }
        : null,
    },
    UNIFI_STALE_MS,
  );
  if (status.state === 'critical') return t('locations.card.down');
  if (uptime) return t('dashboard.sites_uptime', { pct: uptime });
  if (status.state === 'good') return t('locations.card.live');
  return t('locations.card.no_telemetry');
}

function markerCode(location: LocationWithSummary): string {
  if (location.directory?.code) return location.directory.code;
  return location.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function LocationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { accounts, activeAccount, isPortfolio, ready } = useAccount();
  const { data, isLoading, isError, error } = useLocations();
  const { data: openTickets } = useTickets({ status: OPEN_TICKET_STATUSES });
  const { data: unifiSites } = useUnifiSites();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const mapSelectRef = useRef<HTMLSelectElement>(null);

  const locations = useMemo(() => data?.locations ?? [], [data?.locations]);
  const unifi = useMemo(() => unifiByLocation(unifiSites ?? []), [unifiSites]);
  // Freshness of the network telemetry behind every per-site count on this page.
  const unifiAll = useMemo(() => aggregateUnifi(unifiSites ?? []), [unifiSites]);
  const { catalog } = usePropertyCatalog();

  const mapped = useMemo(() => locations.filter((l) => l.coordinates), [locations]);
  const markers = useMemo<GlobeMarker[]>(
    () =>
      mapped.map((l) => ({
        id: l.id,
        code: markerCode(l),
        name: l.name,
        lat: l.coordinates!.lat,
        lng: l.coordinates!.lng,
      })),
    [mapped],
  );
  const selectedLocation = mapped.find((l) => l.id === selectedId) ?? null;

  // In-scope properties with a recorded on-site visit, from
  // property_visit_history keyed by directory code.
  const catalogCodes = useMemo(() => {
    const linked = new Set(
      locations.map((l) => l.directory?.code).filter((code): code is string => !!code),
    );
    return codesWithVisits(catalog).filter((code) => linked.has(code));
  }, [catalog, locations]);
  const selectedCatalogCode = resolvePropertyCode(catalog, selectedLocation?.directory?.code);

  const visibleLocations = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return locations;
    return locations.filter((location) =>
      [location.name, location.type, formatAddress(location), location.directory?.code ?? '']
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalized)),
    );
  }, [locations, query]);

  const ticketsByLocation = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ticket of openTickets ?? []) {
      if (!ticket.location_id) continue;
      counts.set(ticket.location_id, (counts.get(ticket.location_id) ?? 0) + 1);
    }
    return counts;
  }, [openTickets]);

  const subtitle = !ready
    ? t('common.loading')
    : accounts.length === 0
      ? t('account.none')
      : `${locations.length === 1 ? t('locations.subtitle_count_one') : t('locations.subtitle_count', { n: locations.length })}${
          !isPortfolio && activeAccount ? ` ${t('locations.for_account', { name: activeAccount.name })}` : ''
        }`;

  const loading = !ready || isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">{t('locations.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{subtitle}</p>
        </div>
        {!loading && unifiAll.siteCount > 0 && (
          <Freshness
            lastUpdatedAt={unifiAll.latestUpdatedAt}
            label="UniFi"
            expectedRefreshMs={UNIFI_EXPECTED_REFRESH_MS}
            staleMs={UNIFI_STALE_MS}
          />
        )}
      </div>

      {!loading && mapped.length > 0 && (
        <section className="space-y-3" aria-labelledby="portfolio-map-heading">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="portfolio-map-heading" className="font-semibold text-text">{t('locations.map_title')}</h2>
              <p className="text-sm text-text-muted">{t('locations.map_subtitle')}</p>
            </div>
            <label className="w-full sm:w-80">
              <span className="sr-only">{t('locations.map_choose')}</span>
              <select
                ref={mapSelectRef}
                value={selectedId ?? ''}
                onChange={(event) => setSelectedId(event.target.value || null)}
                className="min-h-11 w-full rounded-md border border-border bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-10"
              >
                <option value="">{t('locations.map_choose')}</option>
                {markers.map((marker) => (
                  <option key={marker.id} value={marker.id}>
                    {marker.code} · {marker.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* ≤60vh on phones so the list below stays reachable; capped on desktop. */}
          <div className="relative h-[min(60vh,520px)] min-h-[300px] overflow-hidden rounded-lg border border-border">
            <PropertyGlobe markers={markers} selectedId={selectedId} onSelect={(marker) => setSelectedId(marker.id)} />
            {selectedLocation && (
              <div className="absolute inset-2 z-30 flex items-start sm:inset-y-3 sm:left-auto sm:right-3 sm:w-[360px]">
                <PropertyDetailPanel
                  location={selectedLocation}
                  unifi={unifi.get(selectedLocation.id) ?? null}
                  tickets={openTickets ?? []}
                  onClose={() => {
                    setSelectedId(null);
                    mapSelectRef.current?.focus({ preventScroll: true });
                  }}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {!loading && isPortfolio && catalogCodes.length > 0 && (
        <PortfolioTripCalendar
          propertyCodes={catalogCodes}
          selectedProperty={selectedCatalogCode}
          onSelectProperty={(code) => {
            if (!code) {
              setSelectedId(null);
              return;
            }
            const target = mapped.find((l) => resolvePropertyCode(catalog, l.directory?.code) === code);
            setSelectedId(target?.id ?? null);
          }}
        />
      )}

      <section className="space-y-4" aria-labelledby="account-locations-heading">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 id="account-locations-heading" className="font-semibold text-text">{t('locations.list_title')}</h2>
            <p className="text-sm text-text-muted">{t('locations.list_subtitle')}</p>
          </div>
          <label className="w-full sm:w-72">
            <span className="sr-only">{t('locations.search')}</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('locations.search')}
              className="min-h-11 w-full rounded-md border border-border bg-surface px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary sm:min-h-10"
            />
          </label>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState icon={<LocationIcon />} title={t('account.none')} subtitle={t('account.none_hint')} />
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState
              icon={<LocationIcon />}
              title={t('locations.error_title')}
              subtitle={error instanceof Error ? error.message : t('locations.error_subtitle')}
            />
          </div>
        ) : locations.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState icon={<LocationIcon />} title={t('locations.empty.title')} subtitle={t('locations.empty.subtitle')} />
          </div>
        ) : visibleLocations.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-text-muted">
            {t('locations.no_match', { q: query })}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visibleLocations.map((location) => {
              const network = unifi.get(location.id);
              const showAccount = isPortfolio;
              const accountName = accounts.find((a) => a.customer_id === location.customer_id)?.name;
              return (
                <button
                  key={location.id}
                  type="button"
                  onClick={() => navigate(`/locations/${location.id}`)}
                  className="w-full rounded-lg border border-border bg-surface p-4 text-left transition-colors hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate font-medium text-text">{location.name}</h3>
                      <p className="mt-1 truncate text-sm text-text-muted">
                        {formatAddress(location) || t('locations.address_missing')}
                      </p>
                      {showAccount && accountName && accountName !== location.name && (
                        <p className="mt-0.5 truncate text-xs text-text-faint">{accountName}</p>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-2">
                      <StatusBadge status={location.status ?? 'unknown'} />
                      {location.directory?.brand && (
                        <BrandLogo brand={location.directory.brand} name={location.name} className="h-7" />
                      )}
                    </div>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs text-text-faint">{t('locations.card.type')}</dt>
                      <dd className="mt-0.5 text-text">{formatType(location.type)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-faint">{t('locations.card.network')}</dt>
                      <dd className="mt-0.5 text-text">{networkLabel(network, t)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-faint">{t('locations.card.open_tickets')}</dt>
                      <dd className="mt-0.5 text-text">{ticketsByLocation.get(location.id) ?? 0}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-text-faint">{t('locations.card.timezone')}</dt>
                      <dd className="mt-0.5 truncate text-text">{location.timezone ?? t('common.none')}</dd>
                    </div>
                  </dl>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function formatAddress(location: LocationWithSummary): string {
  const address = location.address;
  if (!address) return '';
  return [address.street, [address.city, address.state].filter(Boolean).join(', '), address.zip]
    .filter(Boolean)
    .join(' ');
}

function formatType(type: string | null | undefined): string {
  if (!type) return '—';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function LocationIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}
